/**
 * ChatPill — A minimal "chat again" button shown after a conversation ends.
 * Displays the avatar photo and staff name as a glassmorphic pill.
 *
 * Much less invasive than the full avatar animation — for users who
 * already know the agent and just want to chat again.
 */

export class ChatPill {
  private container: HTMLDivElement | null = null

  /** Called when the pill is clicked. */
  onClick: (() => void) | null = null

  /**
   * Show the chat pill.
   * @param avatarUrl - URL to the avatar photo
   * @param staffName - Display name of the AI staff
   */
  show(avatarUrl: string | null, staffName: string): void {
    if (this.container) return

    this.container = document.createElement('div')
    Object.assign(this.container.style, {
      position: 'fixed',
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%) scale(0.9)',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 16px 6px 6px',
      background: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(200, 208, 218, 0.5)',
      borderRadius: '24px',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      opacity: '0',
      transition: 'opacity 0.3s ease, transform 0.3s ease, box-shadow 0.2s ease',
      pointerEvents: 'auto',
    })

    // Avatar photo
    if (avatarUrl) {
      const img = document.createElement('img')
      img.src = avatarUrl
      img.alt = staffName
      Object.assign(img.style, {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: '0',
      })
      this.container.appendChild(img)
    } else {
      // Fallback: colored circle with initial
      const circle = document.createElement('div')
      Object.assign(circle.style, {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #4a6fa5, #6b8fc5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '13px',
        fontWeight: '600',
        flexShrink: '0',
      })
      circle.textContent = staffName.charAt(0).toUpperCase()
      this.container.appendChild(circle)
    }

    // Staff name
    const name = document.createElement('span')
    name.textContent = staffName
    Object.assign(name.style, {
      fontSize: '13px',
      fontWeight: '500',
      color: '#2a2a3e',
      whiteSpace: 'nowrap',
    })
    this.container.appendChild(name)

    // Chat icon
    const icon = document.createElement('span')
    icon.textContent = '💬'
    Object.assign(icon.style, {
      fontSize: '14px',
      marginLeft: '2px',
    })
    this.container.appendChild(icon)

    // Click handler
    this.container.addEventListener('click', () => {
      this.onClick?.()
    })

    // Hover effects
    this.container.addEventListener('mouseenter', () => {
      if (this.container) {
        this.container.style.transform = 'translateX(-50%) scale(1.05)'
        this.container.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.12)'
      }
    })
    this.container.addEventListener('mouseleave', () => {
      if (this.container) {
        this.container.style.transform = 'translateX(-50%) scale(1)'
        this.container.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.08)'
      }
    })

    // Append to body (not widget root which may be hidden after conversation)
    document.body.appendChild(this.container)

    // Animate in
    requestAnimationFrame(() => {
      if (this.container) {
        this.container.style.opacity = '1'
        this.container.style.transform = 'translateX(-50%) scale(1)'
      }
    })
  }

  /** Hide and remove the pill. */
  hide(): void {
    if (!this.container) return
    this.container.style.opacity = '0'
    this.container.style.transform = 'translateX(-50%) scale(0.9)'
    setTimeout(() => {
      this.container?.remove()
      this.container = null
    }, 300)
  }

  get isVisible(): boolean {
    return this.container !== null
  }
}
